import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import LeopartLoader from "../../../shared/LeopartLoader/LeopartLoader";
import {
  CalendarDays,
  Layers,
  Dumbbell,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IWorkout } from '../../../models/programming';
import { WORKOUT_TYPE, BLOCK_TYPE_COLORS, BLOCK_TYPE_LABELS } from '../../../enums/programming';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import Typhography from '../../../shared/Typhography/Typhography';
import styles from './WorkoutDetailModal.module.scss';

interface WorkoutDetailModalProps {
  workoutId: string | null;
  onClose: () => void;
}

const WorkoutDetailModal: FC<WorkoutDetailModalProps> = ({ workoutId, onClose }) => {
  const { getProgrammingWorkout } = useEndpoints();

  const { data: workout, isLoading, isError } = useQuery<IWorkout>({
    queryKey: ['programming-workout-detail', workoutId],
    queryFn: async () => {
      const { data } = await getProgrammingWorkout(workoutId!);
      return data;
    },
    enabled: !!workoutId,
    staleTime: 5 * 60 * 1000,
  });

  const getExName = (ex: any) => {
    // exerciseId può essere populated (oggetto) o stringa
    const exId = ex.exerciseId;
    const isPopulated = typeof exId === 'object' && exId !== null;
    const exerciseObj = isPopulated ? exId : ex.exercise;
    return exerciseObj?.name?.it || exerciseObj?.name?.en || (typeof exId === 'string' ? exId : '—');
  };

  return (
    <GenericModalV2
      isOpen={!!workoutId}
      onClose={onClose}
      title={workout?.title || 'Dettaglio scheda'}
      size="xl"
    >
      {isLoading && (
        <div className={styles.loadingState}>
          <LeopartLoader />
        </div>
      )}

      {isError && (
        <div className={styles.errorState}>
          <AlertTriangle size={24} strokeWidth={1.5} />
          <Typhography size="bold-14">Errore nel caricamento</Typhography>
        </div>
      )}

      {workout && (
        <div className={styles.content}>
          {/* Meta */}
          <div className={styles.metaRow}>
            <div className={styles.metaBadge}>
              {workout.type === WORKOUT_TYPE.MULTI_DAY
                ? <Layers size={13} strokeWidth={2.5} />
                : <CalendarDays size={13} strokeWidth={2.5} />
              }
              {workout.type === WORKOUT_TYPE.MULTI_DAY ? 'Multi-giorno' : 'Giorno singolo'}
            </div>
            <span className={styles.metaItem}>
              <CalendarDays size={13} strokeWidth={2.5} />
              {workout.days.length} giorn{workout.days.length === 1 ? 'o' : 'i'}
            </span>
            {workout.estimatedDuration && (
              <span className={styles.metaItem}>
                <Clock size={13} strokeWidth={2.5} />
                ~{workout.estimatedDuration} min
              </span>
            )}
          </div>

          {/* Tags */}
          {workout.tags.length > 0 && (
            <div className={styles.tagsRow}>
              {workout.tags.map((tag) => (
                <span key={tag} className={styles.tag}>{tag}</span>
              ))}
            </div>
          )}

          {/* Athlete notes */}
          {workout.athleteNotes && (
            <div className={styles.notesBox}>
              <Typhography size="bold-12" className={styles.notesLabel}>Note atleta</Typhography>
              <Typhography size="regular-14">{workout.athleteNotes}</Typhography>
            </div>
          )}

          {/* Days */}
          <div className={styles.daysList}>
            {workout.days.map((day, dayIdx) => (
              <div key={dayIdx} className={styles.daySection}>
                <div className={styles.daySectionHeader}>
                  <Typhography size="bold-14">
                    {day.title || `Giorno ${day.dayNumber}`}
                  </Typhography>
                  <span className={styles.dayBlockCount}>
                    {day.blocks.length} blocc{day.blocks.length === 1 ? 'o' : 'hi'}
                  </span>
                </div>

                <div className={styles.blocksList}>
                  {day.blocks.map((block, blockIdx) => {
                    const blockColor = block.color || BLOCK_TYPE_COLORS[block.type] || '#757575';
                    return (
                      <div key={blockIdx} className={styles.blockCard}>
                        <div
                          className={styles.blockStrip}
                          style={{ background: blockColor }}
                        />
                        <div className={styles.blockBody}>
                          <div className={styles.blockHeader}>
                            <Typhography size="bold-12">{block.title}</Typhography>
                            <span
                              className={styles.blockTypeBadge}
                              style={{
                                background: blockColor + '18',
                                color: blockColor,
                                border: `1px solid ${blockColor}30`,
                              }}
                            >
                              {BLOCK_TYPE_LABELS[block.type] || block.type}
                            </span>
                          </div>

                          {block.exercises.length > 0 && (
                            <div className={styles.exercisesList}>
                              {block.exercises.map((ex, exIdx) => (
                                <div key={exIdx} className={styles.exerciseRow}>
                                  <span className={styles.exerciseOrder}>{ex.order + 1}</span>
                                  <span className={styles.exerciseName}>{getExName(ex)}</span>
                                  <span className={styles.exerciseDetail}>
                                    {ex.sets > 0 && `${ex.sets}×${ex.reps || '—'}`}
                                  </span>
                                  {ex.loads && ex.loads.length > 0 && ex.loads[0].value > 0 && (
                                    <span className={styles.exerciseLoad}>
                                      {ex.loads[0].value} {ex.loads[0].unit}
                                    </span>
                                  )}
                                  {ex.rest && (
                                    <span className={styles.exerciseRest}>
                                      <Clock size={10} strokeWidth={2} />
                                      {ex.rest}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </GenericModalV2>
  );
};

export default WorkoutDetailModal;
